<script lang="ts" setup>
import {computed, onMounted, ref, watch, onUnmounted} from 'vue';
import {useSession} from '/src/core/session';
import router from '/src/router';
import {logEvent} from '/src/utils';
import Icon from '/src/components/layout/Icon.vue';
import {getUserSubscription} from '/src/api/users';

const session = useSession();
const auth = computed(() => session.state.auth);
const isChecking = ref(true);
const checkCount = ref(0);
const progress = ref(0);
let checkInterval: NodeJS.Timeout | null = null;
let progressInterval: NodeJS.Timeout | null = null;

function checkSubscription() {
    if (!auth.value?.user?.id) return;

    checkCount.value++;
    getUserSubscription(auth.value.user.id)
        .then((sub) => {
            session.state.subscription = sub;

            // If subscription is active, redirect to projects
            if (sub && sub.status === 'active') {
                logEvent('subscription_verified', sub.id);
                isChecking.value = false;

                if (checkInterval) {
                    clearInterval(checkInterval);
                    checkInterval = null;
                }
                if (progressInterval) {
                    clearInterval(progressInterval);
                    progressInterval = null;
                }

                // router.push('/projects');
            }
        })
        .catch((error) => {
            console.error('Error checking subscription:', error);
        });
}

function startProgressBar() {
    progress.value = 0;
    progressInterval = setInterval(() => {
        if (progress.value < 100) {
            progress.value += 1; // Increment by 1% every 100ms = 10 seconds total
        }
    }, 150);
}

function startChecking() {
    // Start progress bar
    startProgressBar();

    // Check immediately
    checkSubscription();

    // Then check every 5 seconds
    checkInterval = setInterval(() => {
        if (!auth.value) {
            router.push('/');
            return;
        }
        checkSubscription();
    }, 5000);
}

onMounted(() => {
    startChecking();
});

onUnmounted(() => {
    if (checkInterval) {
        clearInterval(checkInterval);
        checkInterval = null;
    }
    if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
    }
});

// Watch for manual subscription updates
watch(
    () => session.state.subscription,
    (newSub) => {
        if (newSub && (newSub.status === 'active' || newSub.status === 'trialing')) {
            isChecking.value = false;

            if (checkInterval) {
                clearInterval(checkInterval);
                checkInterval = null;
            }
            if (progressInterval) {
                clearInterval(progressInterval);
                progressInterval = null;
            }

            router.push('/projects');
        }
    }
);
</script>

<template>
    <div class="flex flex-col items-center mt-16 min-h-screen px-4">
        <div class="max-w-md w-full text-center">
            <!-- Loading spinner -->
            <div class="mb-6">
                <div class="loading loading-spinner r-b-2 text-primary loading-lg mx-auto"></div>
            </div>

            <!-- Main message -->
            <h1 class="text-2xl font-bold mb-4">Processing Your Payment</h1>

            <p class="mb-6">
                Please wait while we verify your subscription.
                <br />
                This usually takes a few moments.
            </p>

            <!-- Status indicator -->
            <div class="rounded-lg p-4 shadow-sm mb-6">
                <div class="flex items-center justify-center text-sm text-primary mb-3">
                    <Icon
                        type="clock"
                        class="w-4 h-4 mr-2"
                    />
                    <span>Checking subscription status...</span>
                </div>

                <!-- Progress bar -->
                <div class="w-full bg-gray-200 rounded-full h-2">
                    <div
                        class="bg-primary h-2 rounded-full transition-all duration-100 ease-out"
                        :style="{width: `${progress}%`}"
                    ></div>
                </div>

                <!-- Progress text -->
                <div class="text-xs text-gray-500 mt-2 text-center">{{ Math.round(progress) }}% complete</div>
            </div>

            <!-- Help text -->
            <div class="text-sm text-gray-500">
                <p class="mb-2">Contact support@lopaka.app if you continue to have issues</p>
            </div>
        </div>
    </div>
</template>
